import axios from 'axios'

/**
 * Instancia central de axios.
 * El token se mantiene actualizado en api.defaults.headers.common['Authorization']
 * desde tres puntos: authStore.login(), authStore.setTokens() (después de refresh)
 * y por la hidratación de sesión desde la cookie HttpOnly al recargar la página.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api/v1',
  timeout: 15_000,
  withCredentials: true,
})

// ─── Callbacks de auth (registrados por authStore para evitar importación circular) ──

type AuthCallbacks = {
  /** Actualiza el access token en memoria tras un refresh exitoso. */
  onTokenRefreshed: (accessToken: string) => void
  /** Limpia la sesión local y redirige al login (refresh fallido o sesión expirada). */
  onSessionExpired: () => void
}

let authCallbacks: AuthCallbacks | null = null

/**
 * Registra las funciones de auth necesarias para el interceptor de refresco.
 * Debe llamarse una sola vez, al inicializar el authStore.
 */
export function registerAuthCallbacks(callbacks: AuthCallbacks): void {
  authCallbacks = callbacks
}

// ─── Request interceptor — el header ya está seteado globalmente ──────────────

api.interceptors.request.use((config) => config)

// ─── Response interceptor — refresco automático de access token ───────────────

/** Cola de peticiones bloqueadas mientras se ejecuta el refresco. */
let isRefreshing = false
let pendingQueue: Array<{
  resolve: (token: string) => void
  reject:  (err: unknown)  => void
}> = []

function processQueue(error: unknown, token: string | null = null): void {
  for (const { resolve, reject } of pendingQueue) {
    if (error) reject(error)
    else resolve(token!)
  }
  pendingQueue = []
}

/**
 * Rutas de auth que NUNCA deben disparar un reintento con refresh.
 * Si el login o refresh devuelven 401, es un error real, no expiración de token.
 */
function isAuthRoute(url: string | undefined): boolean {
  if (!url) return false
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password')
  )
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean }

    const status = error.response?.status

    // Modo mantenimiento: el backend bloquea al rol CLIENTE con 503 / code MAINTENANCE.
    // Redirigir a la pantalla de mantenimiento (que no hace llamadas a la API → sin bucles).
    const code = (error.response?.data as { code?: string } | undefined)?.code
    if (status === 503 && code === 'MAINTENANCE') {
      if (window.location.pathname !== '/mantenimiento') {
        window.location.replace('/mantenimiento')
      }
      return Promise.reject(error)
    }

    // Solo intentar refresh en 401 de endpoints protegidos, y solo una vez por petición
    if (status === 401 && !originalRequest?._retry && !isAuthRoute(originalRequest?.url)) {

      if (isRefreshing) {
        // Otra petición ya está haciendo el refresh — esperar en cola
        return new Promise<string>((resolve, reject) => {
          pendingQueue.push({ resolve, reject })
        })
          .then((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Llamada directa con axios base para evitar que el interceptor se autorreferencia
        const { data } = await axios.post(
          `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api/v1'}/auth/refresh`,
          {},
          { timeout: 10_000, withCredentials: true },
        )

        const newAccessToken: string = data.data.accessToken

        // Actualizar header global para todas las peticiones siguientes
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`

        authCallbacks?.onTokenRefreshed(newAccessToken)

        processQueue(null, newAccessToken)

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
        return api(originalRequest)

      } catch (refreshError) {
        processQueue(refreshError, null)
        // Refresh fallido → sesión expirada sin remedio → desloguear
        authCallbacks?.onSessionExpired()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)
